import { ReactNode, type ComponentType, type PropsWithChildren, useCallback, useEffect, useState, useMemo } from "react"
import { ErrorBoundary, type FallbackProps } from "react-error-boundary"
import { Button, Grid, GridProps, Typography, TypographyProps } from "@mui/material"
//import { faUserRobotXmarks } from "@fortawesome/pro-light-svg-icons"
import { QueryErrorResetBoundary } from "@tanstack/react-query"
import { makeStyles } from "tss-react/mui"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"
import { AppError, NoDataError } from "../errorTypes"
import { StrictOmit } from "../../../lib/lang"
import { hashQueryKey } from "@tanstack/react-query"
import { applyMatchers, ErrorMatch, MatchOptions } from "../../../modules/error/errorMatchers"

const useStyles = makeStyles()((_theme) => ({
    contentWrapper: {
        width: "100%",
        height: "100%",
        padding: "10%",
        textAlign: "center",
    },
}))

type FallbackMessageProps = Readonly<
    PropsWithChildren<{
        slotProps?: {
            contentWrapper?: GridProps
            text?: TypographyProps
        }
        subtitle?: string
    }>
>

export function QueryFallbackMessage({ slotProps = {}, subtitle, children }: FallbackMessageProps) {
    const { contentWrapper: contentWrapperProps = {}, text: textProps = {} } = slotProps
    const { classes, cx } = useStyles()

    return (
        <Grid
            container
            direction="column"
            wrap="nowrap"
            justifyContent="center"
            alignItems="center"
            gap="2rem"
            {...contentWrapperProps}
            className={cx(classes.contentWrapper, contentWrapperProps.className)}
        >
            <Typography variant="subtitle1" color="text.secondary" textAlign="left" {...textProps}>
                {children}
            </Typography>
            {subtitle && (
                <Typography variant="body1" color="text.secondary" align="center">
                    {subtitle}
                </Typography>
            )}
        </Grid>
    )
}

export type QueryErrorFallbackProps = StrictOmit<FallbackMessageProps, "subtitle" | "children"> &
    FallbackProps &
    Readonly<{
        showError?: boolean
        match: ErrorMatch
    }>

export function QueryErrorFallback({
    error,
    showError = false,
    match,
    resetErrorBoundary,
    ...rest
}: QueryErrorFallbackProps) {
    const subtitle = showError ? match.displayError.error.displayMessage : undefined

    return (
        <LocalizedStrict id="query-error-boundary-message">
            <QueryFallbackMessage subtitle={subtitle} {...rest}>
                Could not fetch data
            </QueryFallbackMessage>
        </LocalizedStrict>
    )
}

export function NoDataFallback(props: StrictOmit<FallbackMessageProps, "children">) {
    return (
        <LocalizedStrict id="query-error-boundary-no-data-message">
            <QueryFallbackMessage {...props}>No data</QueryFallbackMessage>
        </LocalizedStrict>
    )
}

type WrapperProps = FallbackProps &
    Readonly<
        PropsWithChildren<{
            noDataFallback: ReactNode
            dependencies?: object
        }>
    >

function FallbackWrapper({ error, noDataFallback, dependencies, children, resetErrorBoundary }: WrapperProps) {
    const [_dependencyHash, setDependencyHash] = useState<string>()
    const nextHash = hashQueryKey([dependencies])

    useEffect(() => {
        setDependencyHash((prevHash) => {
            if (prevHash && nextHash !== prevHash) {
                // Reset error boundary when dependencies change
                if (error) resetErrorBoundary()
            }

            return nextHash
        })
    }, [nextHash, error, setDependencyHash, resetErrorBoundary])

    if (error instanceof NoDataError) {
        return <>{noDataFallback}</>
    }

    return <>{children}</>
}

type QueryErrorBoundaryProps = StrictOmit<FallbackMessageProps, "children"> &
    MatchOptions &
    Readonly<
        PropsWithChildren<{
            Fallback?: ComponentType<QueryErrorFallbackProps>
            noDataFallback?: ReactNode
            dependencies?: object
        }>
    >

export function QueryErrorBoundary({
    Fallback,
    noDataFallback,
    dependencies,
    errorDisplay,
    errorMessagePrefix,
    errorMatchers,
    children,
    ...messageProps
}: QueryErrorBoundaryProps) {
    const noDataFallbackToUse = noDataFallback ?? <NoDataFallback />

    function renderFallback(props: FallbackProps): ReactNode {
        const match = applyMatchers(props.error, { errorDisplay, errorMessagePrefix, errorMatchers })

        return (
            <FallbackWrapper dependencies={dependencies} noDataFallback={noDataFallbackToUse} {...props}>
                {Fallback ? (
                    <Fallback match={match} {...props} {...messageProps} />
                ) : (
                    <QueryErrorFallback match={match} {...props} {...messageProps} />
                )}
            </FallbackWrapper>
        )
    }

    return (
        <QueryErrorResetBoundary>
            {({ reset }) => (
                <ErrorBoundary fallbackRender={renderFallback} onReset={reset}>
                    {children}
                </ErrorBoundary>
            )}
        </QueryErrorResetBoundary>
    )
}
